import React, { useState, useEffect } from 'react';
import { Check, Loader2, BookOpen, Heart, Moon, Sun, Feather } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

interface JourneyPageProps {
    onEnd: () => void;
  }
  
  /*
    Journey content source (important for future edits):
    - Previously: Step 2/3/4 used `journeySteps[].prompt` and called the LLM (via chatfn) to generate answers at runtime.
    - Now: Step 2/3/4 display pre-written, locale-specific content from i18n JSON files:
      `client/public/locales/<lng>/journey.json` (keys: step2_answer, step3_answer, step4_answer).
    - Step 1 and Step 5 remain hardcoded intro/outro strings in this component.

    Editing content:
    - Update the text in the relevant `journey.json` files. Answers are stored as arrays of lines and are joined with blank lines for Markdown rendering.

    Adding more steps:
    - This flow is currently a fixed 5-step journey. To add steps, update:
      - `totalSteps`
      - `journeySteps` (title/icon for the stepper)
      - `getStepContent` mapping (and add `stepN_answer` keys to every locale's `journey.json`)
  */
  export const JourneyPage = ({ onEnd }: { onEnd: () => void }) => {
  const { t } = useTranslation('journey');
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [responseText, setResponseText] = useState('');
  const totalSteps = 5;

  const journeySteps = [
    {
      prompt: "Let's take a journey into ancient wisdom.",
      title: "Ancient Journey",
      icon: BookOpen
    },
    {
      prompt: "Tell me about the main points of the Bhagavad Gita including its approximate dates if available.",
      title: "Bhagavad Gita",
      icon: Sun
    },
    {
      prompt: "Explain the story of Ramayana in brief.",
      title: "Ramayana",
      icon: Moon
    },
    {
      prompt: "Explain the story  and  something interesting from the Yoga Vasistha from context",
      title: "Yoga Vasistha",
      icon: Feather
    },
    {
      prompt: "",
      title: "Thank You",
      icon: Heart
    }
  ];

  useEffect(() => {
    // Load the first step automatically
    setResponseText("Let's take a journey into ancient wisdom.");
    setCurrentStep(1);
  }, []);

  const joinAnswerLines = (value: unknown) => {
    if (Array.isArray(value)) return value.join('\n\n');
    if (typeof value === 'string') return value;
    return '';
  };

  const getStepContent = (step: number) => {
    if (step === 1) return "Let's take a journey into ancient wisdom.";
    if (step === 2) return joinAnswerLines(t('step2_answer', { returnObjects: true }));
    if (step === 3) return joinAnswerLines(t('step3_answer', { returnObjects: true }));
    if (step === 4) return joinAnswerLines(t('step4_answer', { returnObjects: true }));
    if (step === 5) return 'Thank you for taking this journey into ancient wisdom.';
    return '';
  };

  const showStep = (step: number) => {
    setLoading(false);
    setResponseText(getStepContent(step));
  };

  const handleContinue = () => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      showStep(nextStep);
    }
  };

  const handleEnd = () => {
    // Call the parent function to exit journey mode
    if (onEnd) {
      onEnd();
    }
  };

  const renderStepIcon = (index:any) => {
    const IconComponent = journeySteps[index].icon;
    
    if (index + 1 < currentStep) {
      return (
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white">
          <Check size={16} />
        </div>
      );
    } else if (index + 1 === currentStep) {
      return (
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
          {loading ? 
            <Loader2 size={16} className="animate-spin" /> : 
            <IconComponent size={16} />
          }
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600">
          <IconComponent size={16} />
        </div>
      );
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white  p-4 md:p-8">
      {/* Stepper - Responsive version with spiritual icons */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        {journeySteps.map((step, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <div className={`flex-1 h-px md:h-1 ${index < currentStep ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
            )}
            <div className="flex flex-col items-center">
              {renderStepIcon(index)}
              <span className="mt-1 md:mt-2 text-xs md:text-sm font-medium text-center hidden md:block">
                {step.title}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Content Section */}
      <div className="py-4 md:py-6">
        <h2 className="text-xl md:text-2xl font-bold mb-4 text-center">
          {journeySteps[currentStep - 1].title}
        </h2>
        
        <div style={{textAlign:"center"}} className="bg-gray-50 p-4 md:p-6 rounded-lg mb-6 min-h-64 flex flex-col">
          {loading && responseText === '' ? (
            <div className="flex items-center justify-center flex-grow py-12">
              <Loader2 size={36} className="animate-spin text-blue-500" />
            </div>
          ) : (
            loading ? (
                <span className="inline-flex ml-2">
                  <span className="animate-pulse">.</span>
                  <span className="animate-pulse delay-100">.</span>
                  <span className="animate-pulse delay-200">.</span>
                </span>
              ) : (
                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {responseText}
                  </ReactMarkdown>
              </div>
              )
          )}
        </div>
      </div>

      {/* Navigation Buttons - Only show if not the final step */}
   
    <div className="flex justify-center gap-2 md:gap-4">
    { currentStep < 5 &&  <button
            onClick={()=>{handleEnd()}}
            className="px-4 py-3 bg-gray-50 text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-200"
          >
            End journey
          </button>}
          
          {!loading && currentStep < 5 && (   <button
            onClick={handleContinue}
            className="px-4 py-3 bg-gray-50 text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Continue
          </button>    )}
        </div>
  
      
      {/* Only show End button on the final Thank You step */}
      {!loading && currentStep === 5 && (
        <div className="flex justify-center">
          <button
            onClick={handleEnd}
            className="px-4 py-3 bg-gray-50 text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Complete Journey
          </button>
        </div>
      )}
    </div>
  );
};
